import Immutable from 'immutable';
import { createSelector } from '@reduxjs/toolkit';

import type { CommentablePersonProfile, VideoComment } from 'js/src/client';
import type { CommentUser, SceneComment } from 'js/src/data/comments/comments-types';
import StoreORM from 'js/src/data/store-orm';
import type { ImmutableMap } from 'js/src/libs/consts';
import { ImmutableFromJS } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';

const EMPTY_LIST = Immutable.List<number>();

export const doesSceneHaveComments = (sceneId: string) => (state: RootState) => {
    return getCommentIdsBySceneId(sceneId)(state).size > 0;
};

export const getNumOpenCommentsPerScene = createSelector(
    [(state: RootState) => state.comments.scene_comments],
    (sceneComments) => {
        const orm = new StoreORM<SceneComment, string>(sceneComments);
        const sceneIds = orm.getAllIds();
        let response = Immutable.Map<string, number>();

        sceneIds.forEach((sceneId) => {
            const numOpenComments = orm.getById(sceneId).get('open_comment_ids').size;
            const numResolvedComments = orm.getById(sceneId).get('resolved_comment_ids').size;
            if (numOpenComments > 0 || numResolvedComments > 0) {
                response = response.set(sceneId, numOpenComments);
            }
        });

        return response;
    },
);

export const doesVideoHaveComments = (state: RootState) => {
    const orm = new StoreORM<VideoComment>(state.comments.comments);
    return orm.getAllIds().size > 0;
};

export const getCommentsUiState = (state: RootState) => {
    return state.comments.comments_ui;
};

export const getCommentsAreLoading = (state: RootState) => {
    const commentsUiState = getCommentsUiState(state);
    return commentsUiState.loadingComments;
};

/**
 *
 * @param {string} sceneId : the id of scene that contains all the comments
 * @returns Immutable.List() of all the comments on that scene, EMPTY_LIST if empty
 */
export const getCommentIdsBySceneId = (sceneId: string) => (state: RootState) => {
    const orm = new StoreORM<SceneComment, string>(state.comments.scene_comments);
    const commentIds = orm.getByIdOrUndefined(sceneId) && orm.getById(sceneId).get('comment_ids');

    return commentIds || EMPTY_LIST;
};

export const getOpenCommentIdsBySceneId = (sceneId: string) => (state: RootState) => {
    const orm = new StoreORM<SceneComment, string>(state.comments.scene_comments);
    const commentIds =
        orm.getByIdOrUndefined(sceneId) && orm.getById(sceneId).get('open_comment_ids');
    return commentIds || EMPTY_LIST;
};

export const getResolvedCommentIdsBySceneId = (sceneId: string) => (state: RootState) => {
    const orm = new StoreORM<SceneComment, string>(state.comments.scene_comments);
    const commentIds =
        orm.getByIdOrUndefined(sceneId) && orm.getById(sceneId).get('resolved_comment_ids');
    return commentIds || EMPTY_LIST;
};

export const getMessageByCommentId = (commentId: number) => (state: RootState) => {
    const orm = new StoreORM<VideoComment>(state.comments.comments);
    const comment = orm.getById(commentId);
    return comment.get('message');
};

export const getLastUpdatedByCommentId = (commentId: number) => (state: RootState) => {
    const orm = new StoreORM<VideoComment>(state.comments.comments);
    return orm.getById(commentId).get('last_updated');
};

export const getUserByCommentId =
    (commentId: number) =>
    (state: RootState): ImmutableMap<CommentablePersonProfile> | null => {
        const orm = new StoreORM<VideoComment>(state.comments.comments);
        return orm.getById(commentId).get('user') || null;
    };

export const getUserPhotoUrlByCommentId = (commentId: number) => (state: RootState) => {
    const orm = new StoreORM<VideoComment>(state.comments.comments);
    return orm.getById(commentId).get('user_photo_url');
};

export const getUserDisplayNameByCommentId = (commentId: number) => (state: RootState) => {
    const orm = new StoreORM<VideoComment>(state.comments.comments);
    return orm.getById(commentId).get('external_user_name');
};

export const getUserDisplayEmailByCommentId = (commentId: number) => (state: RootState) => {
    const orm = new StoreORM<VideoComment>(state.comments.comments);
    return orm.getById(commentId).get('external_user_email');
};

export const getUsersFromCommentThread = createSelector(
    (state: RootState) => state.comments.comments_users,
    (comments_users): Immutable.List<ImmutableMap<CommentablePersonProfile>> => {
        const orm = new StoreORM<CommentUser, string>(comments_users);
        const nonTeamMembers = orm.getAllIds();
        return nonTeamMembers.map((userEmail) => {
            const nonTeamMemberInfo = orm.getById(userEmail);
            const fullName = nonTeamMemberInfo.get('external_user_name');
            const email = nonTeamMemberInfo.get('external_user_email');
            const splitIndex = fullName.indexOf(' ');
            const first_name = fullName.slice(0, splitIndex);
            const last_name = fullName.slice(splitIndex + 1);
            return ImmutableFromJS<CommentablePersonProfile>({
                first_name,
                last_name,
                email,
                id: null,
            });
        });
    },
);
